import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for managing Wake Lock API to prevent screen timeout
 * Particularly useful for voice call applications on mobile devices
 */
const useWakeLock = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const wakeLockRef = useRef(null);
  const isRequestingRef = useRef(false);

  useEffect(() => {
    // Check if Wake Lock API is supported
    if ('wakeLock' in navigator) {
      setIsSupported(true);
    } else {
      console.warn('Wake Lock API is not supported in this browser');
    }
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (!isSupported) {
      console.warn('Wake Lock API not supported - cannot prevent screen timeout');
      return false;
    }

    // Prevent multiple simultaneous requests
    if (isRequestingRef.current || wakeLockRef.current) {
      return wakeLockRef.current !== null;
    }

    isRequestingRef.current = true;

    try {
      // Request a screen wake lock
      wakeLockRef.current = await navigator.wakeLock.request('screen');
      setIsActive(true);

      // Listen for wake lock release (can happen automatically)
      wakeLockRef.current.addEventListener('release', () => {
        console.log('Wake lock was released');
        setIsActive(false);
        wakeLockRef.current = null;
      });

      console.log('Wake lock acquired - screen will stay awake');
      return true;
    } catch (err) {
      console.error('Failed to acquire wake lock:', err);
      setIsActive(false);
      return false;
    } finally {
      isRequestingRef.current = false;
    }
  }, [isSupported]);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        setIsActive(false);
        console.log('Wake lock released - screen can now timeout');
        return true;
      } catch (err) {
        console.error('Failed to release wake lock:', err);
        return false;
      }
    }
    return true;
  }, []);

  // Handle page visibility change (wake lock is released when page becomes hidden)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isActive && !wakeLockRef.current && !isRequestingRef.current) {
        // Page became visible again and we had an active wake lock - re-acquire it
        console.log('Page became visible - re-acquiring wake lock');
        await requestWakeLock();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActive, requestWakeLock]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
      }
    };
  }, []);

  return {
    isSupported,
    isActive,
    requestWakeLock,
    releaseWakeLock
  };
};

export default useWakeLock;